import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import uniq from 'lodash/uniq';

const COMMA_DELIMITED_VALUES_REGEX = /[,]+/;

const PasteCreatable = props => {
  const {
    value,
    options,
    onChange,
    isValidNewOption,
    getOptionLabel,
    getOptionValue,
    components: componentsOverride,
  } = props;

  const handlePaste = useCallback(event => {
    const clipboard = event.clipboardData.getData('Text');
    if (!clipboard || !COMMA_DELIMITED_VALUES_REGEX.test(clipboard)) {
      return;
    }
    event.preventDefault();

    const currentValue = value || [];
    const existingValues = new Set(currentValue.map(option =>
      (getOptionValue ? getOptionValue(option) : option.value)));

    const tokens = uniq(clipboard.split(COMMA_DELIMITED_VALUES_REGEX).map(token => token.trim()))
      .filter(Boolean);

    const newOptions = tokens
      .filter(token => !existingValues.has(token))
      .filter(token => !isValidNewOption || isValidNewOption(token, currentValue, options || []))
      .map(token => ({
        label: token,
        value: token,
        className: 'Select__create-option-placeholder',
      }));

    if (onChange && newOptions.length) {
      onChange([...currentValue, ...newOptions], { action: 'create-option' });
    }
  }, [value, options, onChange, isValidNewOption, getOptionValue]);

  const handlePasteRef = useRef(handlePaste);
  handlePasteRef.current = handlePaste;

  const onPaste = useCallback(event => handlePasteRef.current(event), []);

  const Input = useCallback(inputProps => (
    <components.Input {...inputProps} onPaste={onPaste} />
  ), [onPaste]);

  const mergedComponents = useMemo(() => ({ ...componentsOverride, Input }), [componentsOverride, Input]);

  return (
    <CreatableSelect
      {...props}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      components={mergedComponents}
    />
  );
};

PasteCreatable.propTypes = {
  value: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  options: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  onChange: PropTypes.func,
  isValidNewOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  components: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default PasteCreatable;
